import { useRef, useState } from "react";
import { Button } from "./Button";
import { useToast } from "../../context/ToastContext";
import { voiceApi } from "../../services/voiceApi";

/**
 * Uses the browser's native Web Speech API (SpeechRecognition) for the
 * mic capture/transcription UX -- simpler than round-tripping audio through
 * POST /voice/transcribe (which expects a base64 blob) just to get text on
 * screen. Chrome/Edge support it; Safari/Firefox don't, so the button hides
 * itself when unavailable rather than showing a dead control in the field.
 *
 * The resulting transcript is then sent to the real backend pipeline --
 * POST /voice/command -- so phrases like "log health check" or "what's due"
 * actually run keyword+AI interpretation and execute the hive action
 * server-side, the same as mobile's VoiceCommandScreen. The transcript is
 * still handed back via onResult too, so it lands in the caller's note
 * textarea: that keeps dictation working for phrasing that isn't a
 * recognized command (interpreted_action === null) and leaves the user a
 * copy of their words to edit/save even when it *was* a command.
 */
export function VoiceNoteButton({ onResult, hiveId }) {
  const [listening, setListening] = useState(false);
  // The gap between the tap and the microphone actually being live (#177).
  // Its own state rather than folding into `listening`, because the two
  // must LOOK different: "Stop" invites the user to start talking, and
  // during this window nothing they say is captured.
  const [starting, setStarting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const recognitionRef = useRef(null);
  const { notify } = useToast();

  const SpeechRecognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!SpeechRecognition) return null;

  async function handleTranscript(text) {
    onResult(text);

    setProcessing(true);
    try {
      const res = await voiceApi.command({ command_text: text, hive_id: hiveId });
      if (!res.interpreted_action) {
        // Say what it WOULD have understood (#239). The note fallback
        // below means nothing is lost either way, but without this
        // somebody never learns the phrasing -- they just keep saying
        // the thing that does not work, louder.
        const tips = (res.suggestions || []).slice(0, 2);
        notify(
          tips.length
            ? `Saved as a note. For a command, try: ${tips.join(", ")}.`
            : "Didn't catch that as a command -- added it to the note instead.",
          "info",
        );
      } else if (res.success) {
        const label = res.interpreted_action.replace(/_/g, " ");
        const dueCount = Array.isArray(res.result?.due_hives) ? res.result.due_hives.length : null;
        notify(dueCount !== null ? `Voice command: ${label} (${dueCount} due)` : `Voice command: ${label}`, "success");
      } else {
        notify(res.result?.reason || "Couldn't complete that voice command.", "warning");
      }
    } catch (err) {
      if (err.apiErrorCode === "feature_disabled") {
        notify("Voice commands aren't enabled for your plan -- turn them on in Settings.", "warning");
      } else {
        notify(err.apiMessage || "Voice command failed", "danger");
      }
    } finally {
      setProcessing(false);
    }
  }

  function toggle() {
    if (listening || starting) {
      recognitionRef.current?.stop();
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      // Accumulate from event.resultIndex across every final result, rather
      // than reading results[0] and stopping.
      //
      // With interimResults off there is usually one, but the spec permits
      // an engine to deliver a long utterance as several final segments,
      // and resultIndex exists precisely to say where the new ones start.
      // Hardcoding 0 quietly dropped everything after the first segment --
      // a second defect hiding behind the reported one, and the reason a
      // long note could lose its middle as well as its start.
      let text = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (event.results[i].isFinal) text += event.results[i][0].transcript;
      }
      if (text.trim()) handleTranscript(text.trim());
    };

    // THE #177 FIX. start() is asynchronous: the engine is not capturing
    // until onstart fires, typically 200-500ms later while the browser
    // opens the microphone. Flipping the button to "Stop" before then told
    // the user to begin talking into a mic that was not live yet, so their
    // first word landed nowhere. Reported from the field as "consistently
    // loses the first character or word".
    //
    // Mobile never had this because its wrapper drives the affordance off
    // the engine's own start event rather than off the tap.
    recognition.onstart = () => {
      setStarting(false);
      setListening(true);
    };
    recognition.onend = () => {
      setStarting(false);
      setListening(false);
    };
    recognition.onerror = () => {
      setStarting(false);
      setListening(false);
    };

    recognitionRef.current = recognition;
    // Deliberately BEFORE start(), so the intermediate state is visible
    // even if the engine takes its time. The button must never look ready
    // while it is not.
    setStarting(true);
    try {
      recognition.start();
    } catch {
      // start() throws if a previous recognition is still winding down.
      // Silent recovery: the user taps again, which is what they would do
      // anyway, and an error toast here would be noise.
      setStarting(false);
    }
  }

  return (
    <Button
      type="button"
      variant={listening ? "danger" : "secondary"}
      size="sm"
      onClick={toggle}
      disabled={processing}
      // Only true once the engine is actually capturing. A screen-reader
      // user relying on this to know when to speak had the same problem
      // as everyone else, and for the same reason.
      aria-pressed={listening}
    >
      {listening
        ? "⏹ Stop"
        : starting
          // Says "wait", not "go". The whole bug was a control that looked
          // ready half a second before it was.
          ? "Starting…"
          : processing
            ? "Processing…"
            : "🎤 Voice Note"}
    </Button>
  );
}
